import { RubyLoader } from '../components/RubyLoader';
import React, { useState, useEffect } from 'react';
import {
  Users, DollarSign, TrendingUp, CheckCircle, Copy,
  LogOut, Coffee
} from 'lucide-react';
import { api } from '../services/apiService';
import { merchantAppUrl, merchantRoute } from '../utils/merchantUrl';

interface AffiliateProfile {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  status: string;
  totalReferrals: number;
  conversions: number;
  totalEarnings: number;
  pendingEarnings: number;
}

interface Referral {
  id: string;
  leadEmail: string;
  status: string;
  createdAt: number;
  convertedAt?: number;
  commissionAmount?: number;
}

const AffiliateDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('affiliateToken');
    if (!token) {
      window.location.href = merchantRoute('/affiliate/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('affiliateToken');
    if (!token) {
      window.location.href = merchantRoute('/affiliate/login');
      return;
    }
    try {
      setLoading(true);
      const [profileRes, referralsRes] = await Promise.all([
        api.affiliates.getProfile(token),
        api.affiliates.getReferrals(token),
      ]);
      setProfile(profileRes as AffiliateProfile);
      setReferrals((referralsRes as any)?.referrals || []);
    } catch (err: any) {
      if (err?.message?.includes('401') || err?.message?.includes('unauthorized')) {
        localStorage.removeItem('affiliateToken');
        window.location.href = merchantRoute('/affiliate/login');
        return;
      }
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Frontend URL for referral links (not super-admin URL)
  const FRONTEND_URL = merchantAppUrl();

  const copyReferralLink = () => {
    if (!profile?.referralCode) return;
    const link = `${FRONTEND_URL}/#/signup?ref=${profile.referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliateToken');
    localStorage.removeItem('affiliateEmail');
    window.location.href = merchantRoute('/affiliate/login');
  };

  if (loading) {
    return (
      <RubyLoader variant="screen" className="bg-stone-100" />
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-stone-400">{error || 'Failed to load profile'}</p>
          <button onClick={fetchData} className="mt-4 text-amber-500 hover:text-amber-400 font-medium">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const referralLink = `${FRONTEND_URL}/#/signup?ref=${profile.referralCode}`;

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-600 rounded-xl flex items-center justify-center">
              <Coffee className="w-5 h-5 text-gray-900" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Affiliate Dashboard</h1>
              <p className="text-sm text-stone-400">{profile.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-stone-400 hover:text-gray-900 text-sm">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-xl mb-6">
            {error}
            <button onClick={() => setError(null)} className="ml-2 font-medium">Dismiss</button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{profile.totalReferrals}</p>
            <p className="text-sm text-stone-400">Total Referrals</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{profile.conversions}</p>
            <p className="text-sm text-stone-400">Conversions</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-900/30 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">${profile.totalEarnings.toFixed(2)}</p>
            <p className="text-sm text-stone-400">Total Earned</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-rose-900/30 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-rose-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">${profile.pendingEarnings.toFixed(2)}</p>
            <p className="text-sm text-stone-400">Pending</p>
          </div>
        </div>

        {/* Referral Link Card */}
        <div className="bg-white rounded-xl p-6 border border-stone-200 mb-8">
          <h2 className="font-semibold text-gray-900 mb-4">Your Referral Link</h2>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={referralLink}
              className="flex-1 bg-stone-100 border border-stone-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 font-mono"
            />
            <button
              onClick={copyReferralLink}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                copied
                  ? 'bg-green-900/30 text-green-400 border border-green-800'
                  : 'bg-gray-900 hover:bg-gray-800 text-white border border-gray-700'
              }`}
            >
              {copied ? <><CheckCircle className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>
          <p className="text-sm text-stone-400 mt-3">
            Share this link with cafe owners. Code: <span className="font-mono font-medium text-amber-400">{profile.referralCode}</span>
          </p>
        </div>

        {/* Referrals Table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="font-semibold text-gray-900">Your Referrals</h2>
          </div>
          <div className="p-6">
            {referrals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-dark-600 mx-auto mb-3" />
                <p className="text-stone-400">No referrals yet. Share your link to get started!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-stone-400 border-b border-stone-200">
                      <th className="pb-3 font-medium">Email</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {referrals.map((ref) => (
                      <tr key={ref.id} className="text-sm">
                        <td className="py-3 text-gray-900">{ref.leadEmail}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            ref.status === 'converted'
                              ? 'bg-green-900/30 text-green-400'
                              : ref.status === 'signed_up'
                              ? 'bg-blue-900/30 text-blue-400'
                              : 'bg-stone-100 text-stone-400'
                          }`}>
                            {ref.status}
                          </span>
                        </td>
                        <td className="py-3 text-stone-400">
                          {new Date(ref.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 text-right font-medium text-gray-900">
                          {ref.commissionAmount ? `$${ref.commissionAmount.toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AffiliateDashboard;
